import { useIDFilter } from '@src/hooks/filters/useIDFilter';
import { useMarketsFilter } from '@src/hooks/filters/useMarketsFilter';
import { useSidesFilter } from '@src/hooks/filters/useSidesFilter';
import { useSubAccountsFilter } from '@src/hooks/filters/useSubAccountsFilter';
import { isExcludedByResolvedUsers, useResolvedUsers, useUsersFilter } from '@src/hooks/filters/useUsersFilter';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useUser } from '@src/hooks/useUser';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import {
  EMPTY_OBJECT,
  IconName,
  PricingModeEnum,
  QUOTE_STATUS_MAPPING,
  QuoteStatusEnum,
  filterExistsAndExcludes,
  isExcludedByCurrencyOrBasePairFilters,
  useBasePairFilter,
  useCurrencyOfSymbolFilter,
  useGenericFilter,
  useSecuritiesContext,
  useSymbolsFilter,
  type BlotterTableClientLocalFilter,
  type DateRangeFilter,
  type FilterableProperty,
  type Quote,
} from '@talos/kyoko';
import { compact, keys, pick, startCase, uniq, values } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import {
  rowMatchesPredictionMarketBlotterFilters,
  usePredictionMarketBlotterFilterProperties,
  usePredictionMarketBlotterLookup,
  type PredictionMarketBlotterFilter,
} from '../predictionMarketBlotter';

export interface QuotesV2TableFilter extends DateRangeFilter, PredictionMarketBlotterFilter {
  Markets?: string[];
  Sides?: string[];
  Symbols?: string[];
  /** These are the quote status texts we are filtering by on the client side */
  _statuses?: string[];
  /** These are the quote statuses that we are filtering by on the server */
  Statuses?: QuoteStatusEnum[];
  Users?: string[];
  SubAccounts?: string[];
  TradedSides?: string[];
  RFQID?: string[];
  ParentRFQID?: string[];
  PricingMode?: string[];
  Currencies?: string[];
  BasePairs?: string[];
}

export interface QuotesV2ServerFilter {
  StartDate?: string;
  EndDate?: string;
  Users?: string[];
  Statuses?: string[];
  Symbols?: string[];
  RFQID?: string;
  ParentRFQID?: string;
}

export interface UseQuotesV2FilterParams {
  initialFilter: QuotesV2TableFilter | undefined;
  onFilterChanged: (filter: QuotesV2TableFilter) => void;
  showStatusFilter?: boolean;
}

export function useQuotesV2Filter({
  initialFilter,
  onFilterChanged: saveFilter,
  showStatusFilter = true,
}: UseQuotesV2FilterParams) {
  const { subAccountsEnabled } = useSubAccounts();
  const { Name: currentUserName } = useUser();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { enablePredictionMarkets } = useFeatureFlag();
  const predictionMarketFilterProperties = usePredictionMarketBlotterFilterProperties();
  const predictionMarketLookup = usePredictionMarketBlotterLookup();

  // Build filterable properties (same as useQuotesFilter)
  const subAccountsFilter = useSubAccountsFilter(EMPTY_OBJECT);
  const marketsFilter = useMarketsFilter({ onlyTradingMarkets: true });
  const usersFilter = useUsersFilter();
  const symbolFilter = useSymbolsFilter();
  const currencyOfSymbolFilter = useCurrencyOfSymbolFilter();
  const basePairFilter = useBasePairFilter();
  const sidesFilter = useSidesFilter();
  const idFilter = useIDFilter();

  const filterableProperties = useMemo(
    () =>
      compact<FilterableProperty<keyof QuotesV2TableFilter>>([
        marketsFilter,
        subAccountsEnabled ? subAccountsFilter : null,
        symbolFilter,
        currencyOfSymbolFilter,
        basePairFilter,
        usersFilter,
        sidesFilter,
        { ...sidesFilter, key: 'TradedSides', label: 'Traded Side' },
        showStatusFilter
          ? {
              key: '_statuses',
              label: 'Status',
              icon: IconName.CheckCircle,
              options: [...keys(QUOTE_STATUS_MAPPING), ...keys(QuoteStatusEnum)],
              getOptionLabel: (option: string) => startCase(option),
            }
          : undefined,
        { ...idFilter, key: 'RFQID', label: 'RFQ ID' },
        { ...idFilter, key: 'ParentRFQID', label: 'Parent RFQ ID' },
        {
          key: 'PricingMode',
          label: 'Pricing Mode',
          icon: IconName.CurrencyDollar,
          options: values(PricingModeEnum),
          getOptionLabel: (option: string) => startCase(option),
        },
        ...predictionMarketFilterProperties,
      ]),
    [
      marketsFilter,
      subAccountsEnabled,
      subAccountsFilter,
      symbolFilter,
      currencyOfSymbolFilter,
      basePairFilter,
      usersFilter,
      sidesFilter,
      idFilter,
      showStatusFilter,
      predictionMarketFilterProperties,
    ]
  );

  const { filter, filterBuilderProps, blotterTableFilterProps } = useGenericFilter<QuotesV2TableFilter>(
    filterableProperties,
    {
      initialFilter,
      onFilterChanged: saveFilter,
    }
  );
  const statusesFilter = useMemo(() => deriveQuoteStatuses(filter._statuses), [filter._statuses]);

  const resolvedUsers = useResolvedUsers(filter.Users, currentUserName);

  // Client-side filter function operating on bare Quote data
  const filterFn = useCallback(
    (data: Quote) => {
      if (filterExistsAndExcludes(filter, 'Sides', data, 'Side')) {
        return false;
      }

      if (filterExistsAndExcludes(filter, 'PricingMode', data, 'PricingMode')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'TradedSides', data, 'TradedSide')) {
        return false;
      }

      if (
        isExcludedByCurrencyOrBasePairFilters(data?.Symbol, securitiesBySymbol, filter.Currencies, filter.BasePairs)
      ) {
        return false;
      }

      if (isExcludedByResolvedUsers(resolvedUsers, data?.User)) {
        return false;
      }

      if (statusesFilter?.length && (!data.QuoteStatus || !statusesFilter.includes(data.QuoteStatus))) {
        return false;
      }

      // Markets: data.Markets is an array of objects with a Market property
      const marketsFilters: string[] = filter.Markets ?? [];
      if (marketsFilters.length > 0 && !data.Markets.some(market => marketsFilters.some(m => market.Market === m))) {
        return false;
      }

      // SubAccounts: check both direct SubAccount and nested Allocations
      const filteredSubAccounts: string[] = filter.SubAccounts ?? [];
      if (
        filteredSubAccounts.length > 0 &&
        !(
          (data.SubAccount && filteredSubAccounts.includes(data.SubAccount)) ||
          data.Allocation?.Allocations.some(alloc => filteredSubAccounts.includes(alloc.SubAccount))
        )
      ) {
        return false;
      }

      if (
        enablePredictionMarkets &&
        !rowMatchesPredictionMarketBlotterFilters(data.Symbol, filter, predictionMarketLookup)
      ) {
        return false;
      }

      return true;
    },
    [enablePredictionMarkets, filter, predictionMarketLookup, resolvedUsers, securitiesBySymbol, statusesFilter]
  );

  const blotterFilterFn = useCallback<BlotterTableClientLocalFilter<Quote>>(
    row => (row.data ? filterFn(row.data) : true),
    [filterFn]
  );

  return {
    filter,
    filterFn,
    blotterFilterFn,
    filterBuilderProps,
    blotterTableFilterProps,
  };
}

export function quotesV2ServerFilter(filter: QuotesV2TableFilter | undefined): QuotesV2ServerFilter | undefined {
  if (!filter) {
    return undefined;
  }

  const serverFilter: QuotesV2ServerFilter = pick(filter, ['StartDate', 'EndDate', 'Users', 'Symbols']);
  serverFilter.Statuses = deriveQuoteStatuses(filter._statuses);
  serverFilter.RFQID = filter.RFQID?.at(0);
  serverFilter.ParentRFQID = filter.ParentRFQID?.at(0);
  return serverFilter;
}

/**
 * We let the user filter by both "status groups" and standalone QuoteStatuses. Here we flatten the groups into their respective
 * stand-alones, and uniq() the result. Eg "All Open" -> PendingNew, Open, etc...
 */
function deriveQuoteStatuses(statuses: string[] | string | undefined): QuoteStatusEnum[] | undefined {
  if (!statuses || !Array.isArray(statuses) || statuses.length === 0) {
    return undefined;
  }
  return uniq(
    statuses.flatMap(s =>
      s in QUOTE_STATUS_MAPPING ? QUOTE_STATUS_MAPPING[s as keyof typeof QUOTE_STATUS_MAPPING] : s
    )
  ) as QuoteStatusEnum[];
}
